import { useState, createContext, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getVisibleNavItems } from "./navConfig";
import { useTranslation } from "react-i18next";

export const SidebarContext = createContext<{
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
}>({ mobileOpen: false, setMobileOpen: () => {} });

export function useSidebar() {
  return useContext(SidebarContext);
}

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { mobileOpen, setMobileOpen } = useSidebar();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const visibleNav = getVisibleNavItems(user?.role);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`flex flex-col bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 transition-all duration-300 ease-in-out z-50 h-screen fixed md:sticky top-0 ${
          collapsed ? "md:w-[72px]" : "md:w-[260px]"
        } ${
          mobileOpen
            ? "translate-x-0 w-[260px]"
            : "-translate-x-full md:translate-x-0 w-[260px]"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-slate-200 dark:border-slate-800">
          <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/0a8677a0-78d8-4971-947c-fe12fe0ee52e.png"
              alt="MalariaGuard"
              className="w-9 h-9 object-contain rounded"
            />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <h1 className="font-display font-bold text-sm tracking-tight text-slate-900 dark:text-white whitespace-nowrap">
                MalariaGuard
              </h1>
              <p className="text-[10px] text-slate-400 font-mono tracking-wider uppercase whitespace-nowrap">
                EpiSense AI
              </p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  isActive
                    ? "bg-teal-muted/30 dark:bg-teal-muted/40 text-teal border border-teal/20 shadow-[0_0_12px_rgba(45,212,191,0.15)]"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60"
                }`}
                title={collapsed ? t(item.label) : undefined}
              >
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isActive ? "text-teal" : ""}`}>
                  <i className={`${item.icon} text-base`} />
                </div>
                {(!collapsed || mobileOpen) && (
                  <span className="whitespace-nowrap transition-opacity duration-200">
                    {t(item.label)}
                  </span>
                )}
                {isActive && !collapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-teal shadow-glow" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden md:block px-3 py-3 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-all duration-200 w-full text-sm"
            title={collapsed ? t("sidebar_expand") : t("sidebar_collapse")}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i
                className={`ri-arrow-left-s-line text-base transition-transform duration-300 ${
                  collapsed ? "rotate-180" : ""
                }`}
              />
            </div>
            {!collapsed && <span className="whitespace-nowrap">{t("sidebar_collapse")}</span>}
          </button>
        </div>
      </aside>
    </>
  );
}